import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  listProjects,
  searchRag,
  askRag,
  listStrategies,
  listHistory,
  deleteHistory,
  clearHistory,
  listDocuments,
  getDocument,
  createProject,
  setProjectLanguage,
  deleteProject,
  deleteDocument,
  reindexDocument,
  uploadDocuments,
  addPath,
  refreshStale,
  askScope,
  curateDocument,
  scanCodePath,
  importCodebase,
  cloneCodebase,
  codebaseStatus,
  syncCodebase,
  setCodebaseAutoSync,
} from '../services/ragApi'

// One question across multiple projects (agent-user-gaps #2).
export const useAskScope = () => useMutation({ mutationFn: askScope })

export function useProjects() {
  return useQuery({
    queryKey: ['rag', 'projects'],
    queryFn: listProjects,
    staleTime: 60 * 1000,
  })
}

export function useSearch() {
  return useMutation({ mutationFn: searchRag })
}

export function useAsk() {
  return useMutation({ mutationFn: askRag })
}

export function useStrategies() {
  return useQuery({
    queryKey: ['rag', 'strategies'],
    queryFn: listStrategies,
    staleTime: Infinity,
  })
}

export function useHistory(project) {
  return useQuery({
    queryKey: ['rag', 'history', project],
    queryFn: () => listHistory(project),
    enabled: !!project,
  })
}

function useHistoryMutation(mutationFn) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['rag', 'history', vars.project] }),
  })
}

export const useDeleteHistory = () => useHistoryMutation(deleteHistory)
export const useClearHistory = () => useHistoryMutation(({ project }) => clearHistory(project))

// `page` = { limit, offset } for server-side pagination; omit for the full list
// (some callers, e.g. the transcripts library's indexed-set, need every doc).
export function useDocuments(project, page = {}) {
  const { limit, offset } = page
  return useQuery({
    queryKey: ['rag', 'documents', project, limit ?? null, offset ?? 0],
    queryFn: () => listDocuments(project, { limit, offset }),
    enabled: !!project,
    placeholderData: keepPreviousData,
  })
}

export function useDocument(project, source) {
  return useQuery({
    queryKey: ['rag', 'document', project, source],
    queryFn: () => getDocument({ project, source }),
    enabled: !!project && !!source,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createProject,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rag', 'projects'] }),
  })
}

export function useSetProjectLanguage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: setProjectLanguage,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rag', 'projects'] }),
  })
}

export function useDeleteProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (project) => deleteProject(project),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rag', 'projects'] }),
  })
}

function useDocMutation(mutationFn) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['rag', 'documents', vars.project] })
      qc.invalidateQueries({ queryKey: ['rag', 'projects'] })
    },
  })
}

export const useUploadDocuments = () => useDocMutation(uploadDocuments)
export const useAddPath = () => useDocMutation(addPath)
// Codebase scan (dry-run, no cache invalidation needed) + saga import.
export const useScanCodePath = () => useMutation({ mutationFn: scanCodePath })
export const useImportCodebase = () => useDocMutation(importCodebase)
export const useCloneCodebase = () => useDocMutation(cloneCodebase)
// Git-aware status check (on demand) + saga sync.
export const useCodebaseStatus = () => useMutation({ mutationFn: codebaseStatus })
export const useSyncCodebase = () => useDocMutation(syncCodebase)
export const useSetCodebaseAutoSync = () => useMutation({ mutationFn: setCodebaseAutoSync })
export const useDeleteDocument = () => useDocMutation(deleteDocument)
export const useReindexDocument = () => useDocMutation(reindexDocument)
export const useRefreshStale = () => useDocMutation(refreshStale)
export const useCurateDocument = () => useDocMutation(curateDocument)
